import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Flex,
  Separator,
  Text,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import { FaHome } from "react-icons/fa";
import {
  FaFacebook,
  FaInstagram,
  FaSoundcloud,
  FaTiktok,
  FaXTwitter,
  FaYoutube,
} from "react-icons/fa6";
import { FiInfo, FiLink, FiPlus, FiX } from "react-icons/fi";
import { cn, ensureHttpsUrl } from "@/lib/utils";
import type { UpdateCreatorSocialDto } from "@/lib/lutely-api/gen/dtos";

type LinkItem = { id: string; url: string; channelCode: string };
type SavedLink = { url: string; channelCode: string };

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  facebook: FaFacebook,
  instagram: FaInstagram,
  tiktok: FaTiktok,
  youtube: FaYoutube,
  soundcloud: FaSoundcloud,
  twitter: FaXTwitter,
  x: FaXTwitter,
  homepage: FaHome,
};

const HOSTNAME_TO_CHANNEL: Record<string, string> = {
  "instagram.com": "instagram",
  "facebook.com": "facebook",
  "fb.com": "facebook",
  "tiktok.com": "tiktok",
  "youtube.com": "youtube",
  "youtu.be": "youtube",
  "soundcloud.com": "soundcloud",
  "twitter.com": "twitter",
  "x.com": "twitter",
};

function detectChannel(url: string): string {
  try {
    const withScheme = url.startsWith("http") ? url : `https://${url}`;
    const hostname = new URL(withScheme).hostname.replace(/^www\./, "");
    return HOSTNAME_TO_CHANNEL[hostname] ?? "homepage";
  } catch {
    return "homepage";
  }
}

function LinkIcon({ channelCode, url }: { channelCode: string; url: string }) {
  if (!url.trim() || channelCode === "homepage") {
    return <FiLink className="w-4 h-4" />;
  }
  const Icon = iconMap[channelCode.toLowerCase()] ?? FiLink;
  return <Icon className="w-4 h-4" />;
}

function normalizeSort(arr: SavedLink[]) {
  return [...arr]
    .map(({ url, channelCode }) => ({
      url: ensureHttpsUrl(url) || url,
      channelCode,
    }))
    .sort((a, b) => a.channelCode.localeCompare(b.channelCode));
}

const MAX_LINKS = 7;

export type InlineLinksEditSectionProps = {
  initialLinks: SavedLink[];
  isSaving: boolean;
  onSave: (links: UpdateCreatorSocialDto[]) => Promise<void>;
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Reports the live draft so a parent can save every section at once.
   * `hasDuplicates` mirrors the one-per-platform rule this section enforces.
   */
  onValuesChange?: (values: {
    socials: UpdateCreatorSocialDto[];
    hasDuplicates: boolean;
  }) => void;
};

export function InlineLinksEditSection({
  initialLinks,
  isSaving,
  onSave,
  onDirtyChange,
  onValuesChange,
}: InlineLinksEditSectionProps) {
  const [links, setLinks] = useState<LinkItem[]>(() =>
    initialLinks.length > 0
      ? initialLinks.map((l) => ({ ...l, id: crypto.randomUUID() }))
      : [{ id: crypto.randomUUID(), url: "", channelCode: "homepage" }],
  );
  const [baseline, setBaseline] = useState<SavedLink[]>(initialLinks);

  const nonEmptyLinks = links.filter((l) => l.url.trim());

  const isDirty = useMemo(
    () =>
      JSON.stringify(normalizeSort(nonEmptyLinks)) !==
      JSON.stringify(normalizeSort(baseline)),
    [nonEmptyLinks, baseline],
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const channelCodes = nonEmptyLinks.map((l) => l.channelCode);
    onValuesChange?.({
      socials: nonEmptyLinks
        .map((l) => ({
          channelCode: l.channelCode,
          url: ensureHttpsUrl(l.url),
        }))
        .filter((s): s is UpdateCreatorSocialDto => !!s.url),
      hasDuplicates: channelCodes.length !== new Set(channelCodes).size,
    });
  }, [links]); // eslint-disable-line react-hooks/exhaustive-deps

  function addLink() {
    if (links.length >= MAX_LINKS) return;
    setLinks((prev) => [
      ...prev,
      { id: crypto.randomUUID(), url: "", channelCode: "homepage" },
    ]);
  }

  function updateLinkUrl(index: number, url: string) {
    setLinks((prev) =>
      prev.map((l, i) =>
        i === index ? { id: l.id, url, channelCode: detectChannel(url) } : l,
      ),
    );
  }

  function removeLink(index: number) {
    setLinks((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSave() {
    const channelCodes = nonEmptyLinks.map((l) => l.channelCode);
    if (channelCodes.length !== new Set(channelCodes).size) {
      toast.error(
        "You can only have one link per platform and one artist website. Please remove duplicate links.",
      );
      return;
    }
    const socials: UpdateCreatorSocialDto[] = nonEmptyLinks
      .map((l) => ({ channelCode: l.channelCode, url: ensureHttpsUrl(l.url) }))
      .filter((s): s is UpdateCreatorSocialDto => !!s.url);
    // Baseline resets only on a save that landed — a rejected onSave leaves
    // the section dirty so its edits are not silently reported as clean.
    try {
      await onSave(socials);
    } catch {
      return;
    }
    setBaseline(socials);
    setLinks(nonEmptyLinks);
  }

  function handleCancel() {
    setLinks(baseline.map((l) => ({ ...l, id: crypto.randomUUID() })));
  }

  const isSocialLink = (channelCode: string, url: string) =>
    url.trim() && channelCode !== "homepage";

  return (
    <Flex direction="column" gap="4" className="pb-32 sm:pb-8">
      <Separator className="w-full" />

      <Flex align="center" gap="2">
        <Text as="p" size="2" weight="medium" className="text-blue-900">
          Links (Max {MAX_LINKS})
        </Text>
        <Tooltip content="Supported platforms: Instagram, Facebook, TikTok, YouTube, SoundCloud, X (Twitter), and Artist website">
          <button
            type="button"
            className="text-[#525966] hover:text-[#050C2B] transition-colors"
          >
            <FiInfo className="w-3.5 h-3.5" />
          </button>
        </Tooltip>
      </Flex>

      <Flex direction="column" gap="2">
        {links.map((link, idx) => (
          <Flex key={link.id} align="center" gap="2">
            <span
              className={cn(
                "w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full border",
                isSocialLink(link.channelCode, link.url)
                  ? "bg-blue-100 border-blue-300 text-blue-500"
                  : "border-gray-400 text-[#525966]",
              )}
            >
              <LinkIcon channelCode={link.channelCode} url={link.url} />
            </span>
            <TextField.Root
              value={link.url}
              onChange={(e) => updateLinkUrl(idx, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addLink();
                }
              }}
              className="flex-1"
              placeholder="Paste a link"
              autoFocus={link.url === ""}
            />
            <button
              type="button"
              onClick={() => removeLink(idx)}
              className="text-[#06112E]/70 hover:text-[#06112E] flex-shrink-0 transition-colors"
              aria-label="Remove link"
            >
              <FiX className="w-5 h-5" />
            </button>
          </Flex>
        ))}
      </Flex>

      <Flex align="center" justify="between">
        {links.length < MAX_LINKS ? (
          <button
            type="button"
            onClick={addLink}
            className="flex items-center gap-1.5 text-sm text-[#525966] hover:text-[#050C2B] transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Add link
          </button>
        ) : (
          <span />
        )}
        <Flex align="center" gap="3">
          <button
            type="button"
            onClick={handleCancel}
            disabled={!isDirty}
            className="text-sm text-[#525966] hover:text-[#050C2B] transition-colors disabled:opacity-40 disabled:pointer-events-none"
          >
            Cancel
          </button>
          <Button
            size="2"
            radius="full"
            onClick={handleSave}
            disabled={!isDirty || isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}
